
import queryString, {ParsedUrlQuery} from 'query-string';
import {useCallback, useMemo, useRef} from 'react';
import {useNavigate, useLocation, NavigateOptions, To} from 'react-router-dom';
import {parsePath} from 'history';

const normalize = (pathname: string): string => pathname.replace(/\/{2,}/g, '/');
const trimTrailingSlashes = (pathname: string): string => pathname.replace(/\/+$/, '');

export const resolve = (from: string, to: string): string => {
    if (!to) {
        return normalize(trimTrailingSlashes(from));
    }

    if (to.startsWith('/')) {
        return to;
    }

    const segments = normalize(trimTrailingSlashes(from)).split('/');
    const toSegments = normalize(trimTrailingSlashes(to)).split('/');

    for (const segment of toSegments) {
        if (segment === '..') {
            segments.length > 1 && segments.pop();
        }
        else if (segment !== '.') {
            segments.push(segment);
        }
    }

    return segments.length > 1 ? normalize(segments.join('/')) : '/';
};


export type Navigate = (to: To | string, options?: NavigateOptions) => void;

export function useSearchParams(defaults?: SearchQuery): ParsedUrlQuery {
    const defaultsRef = useRef(defaults);
    const location = useLocation();
    const params = useMemo(
        () => {
            const params = queryString.parse(location.search);
            const defaults = defaultsRef.current;

            if (defaults) {
                const defaultEntries = Object.entries(defaults).filter(([key]) => !params[key]);
                for (const [key, value] of defaultEntries) {
                    ([] as string[]).concat(value).forEach(v => (params[key] = v));
                }
            }

            return params;
        },
        [location.search]
    );

    return params;
}

export function useRouter() {
    const navigate = useNavigate();
    const fromURL = useLocation();
    const push = useCallback<Navigate>(
        (to, options = {replace: false}) => {
            const toURL = typeof to === 'string' ? parsePath(to) : to;
            const pathname = resolve(fromURL.pathname, toURL.pathname || '');
            const destination = pathname;
            if ((to as any).query) {
                const prefix = to.search ? to.search + '&' : '';
                to.search = prefix + queryString.stringify((to as any).query);
            }
            const toLocation = {
                ...toURL,
                state: options.state,
                pathname: destination,
            };
            navigate(toLocation);
        },
        [fromURL.pathname, navigate]
    );
    return {
        push,
        query: queryString.parse(location.search),
    };
}

export interface SearchQuery {
    [key: string]: string | string[];
}

export default useRouter;
